import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AppHeader } from '../AppHeader';
import { Button, cx, Icon, Spinner, type IconName } from '../../components/ui';
import { request, errorMessage } from '../../lib/api';
import { DataSourcesPanel } from './DataSourcesPanel';
import { OrganizationPanel } from './OrganizationPanel';
import { Notice } from './pageParts';

type Path = 'personal' | 'join' | 'create';
interface Progress {
  path: Path | null;
  step: number;
  completedAt: number | null;
}

export function safeSetupNext(value: string | null) {
  return value?.startsWith('/') && !value.startsWith('//') && !/[\\\x00-\x1f]/.test(value) && !value.startsWith('/onboarding') ? value : '/missions';
}

const STEPS = ['How you work', 'Aircraft data', 'Start exploring'] as const;

const PATHS: Array<{ path: Path; icon: IconName; title: string; body: string }> = [
  { path: 'join', icon: 'users', title: 'Join an organization', body: 'Find your team and request access. Once approved, use its shared fleet, shoots and admin-supplied data.' },
  { path: 'create', icon: 'plus', title: 'Create an organization', body: 'Set up a team and supply the data sources its members will share.' },
  { path: 'personal', icon: 'user', title: 'Use Airview Studio on my own', body: 'Bring your own provider key for real aircraft tracking.' },
];

const FIRST_STEPS: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: 'aircraft',
    title: 'Fleets',
    body: 'Add an aircraft by registration or ICAO hex, then enable recording. Recording uses your enabled data sources and can use your provider allowance while you are away.',
  },
  {
    icon: 'plan',
    title: 'Shoots',
    body: 'Choose an airport, add aircraft and crew, then go live. Practice scenarios let you learn the controls without a data key.',
  },
  {
    icon: 'location',
    title: 'In the field',
    body: 'Allow location and compass access when you use Point or share a live position. Set your camera and lens for field of view guidance. Replay is available during a live shoot up to the latest recorded moment.',
  },
];

export function OnboardingPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = safeSetupNext(params.get('next'));
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void request<Progress>('GET', '/me/onboarding')
      .then(setProgress)
      .catch((e) => setError(errorMessage(e)));
  }, []);

  async function save(path: Path | null, step: number, complete = false) {
    setBusy(true);
    setError('');
    try {
      await request('PUT', '/me/onboarding', { path, step, complete });
      setProgress({ path, step, completedAt: complete ? Date.now() : null });
      if (complete) nav(next, { replace: true });
      else window.scrollTo({ top: 0 });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <AppHeader />
      <main className="page__main onboarding">
        <div className="page__head">
          <div>
            <div className="eyebrow">Welcome to Airview Studio</div>
            <h1>Let’s get you set up</h1>
            <p className="page__lede">Choose how you work, connect aircraft data, then start a shoot. You can return to this guide in My settings.</p>
          </div>
        </div>

        {error ? (
          <Notice tone="error" role="alert" className="onboarding__error">
            {error}
          </Notice>
        ) : null}

        {!progress ? (
          !error ? (
            <div className="loading-row" role="status">
              <Spinner /> Loading your setup…
            </div>
          ) : null
        ) : (
          <>
            <nav className="setup-steps" aria-label="Setup progress">
              <ol>
                {STEPS.map((label, i) => {
                  const done = i < progress.step || (i === 2 && !!progress.completedAt);
                  const current = progress.step === i;
                  return (
                    <li key={label} className={cx('setup-step', current && 'is-current', done && 'is-done')}>
                      <button
                        type="button"
                        className="setup-step__btn"
                        aria-current={current ? 'step' : undefined}
                        aria-label={`${i + 1}. ${label}`}
                        disabled={busy || i > progress.step || (!progress.path && i > 0)}
                        onClick={() => void save(progress.path, i)}
                      >
                        <span className="setup-step__n num" aria-hidden>
                          {done ? <Icon name="check" size={14} strokeWidth={2.2} /> : i + 1}
                        </span>
                        <span className="setup-step__label" aria-hidden>
                          {label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>

            {progress.step === 0 ? (
              <section className="setup-section" aria-labelledby="setup-how">
                <header className="setup-section__head">
                  <h2 id="setup-how" className="setup-section__title">
                    How will you use Airview Studio?
                  </h2>
                  <p className="muted">No invite code is needed to create an account or request organization membership.</p>
                </header>
                <div className="path-cards">
                  {PATHS.map((p) => (
                    <PathCard key={p.path} {...p} selected={progress.path === p.path} disabled={busy} onPick={() => void save(p.path, 1)} />
                  ))}
                </div>
              </section>
            ) : progress.step === 1 ? (
              <section className="setup-section">
                {progress.path === 'personal' ? <DataSourcesPanel /> : <OrganizationPanel setupIntent={progress.path === 'join' ? 'join' : 'create'} />}
              </section>
            ) : (
              <section className="setup-section" aria-labelledby="setup-first">
                <header className="setup-section__head">
                  <h2 id="setup-first" className="setup-section__title">
                    Your first flight in Airview Studio
                  </h2>
                  <p className="muted">Three places you’ll spend your time. Everything here is also in the menu under your name.</p>
                </header>
                <div className="first-steps">
                  {FIRST_STEPS.map((f) => (
                    <article key={f.title} className="first-step">
                      <span className="first-step__icon" aria-hidden>
                        <Icon name={f.icon} size={20} />
                      </span>
                      <h3 className="first-step__title">{f.title}</h3>
                      <p>{f.body}</p>
                    </article>
                  ))}
                </div>
                <Link className="setup-aside" to="/cameras">
                  <Icon name="camera" size={18} />
                  <span>Explore California airport cameras</span>
                  <Icon name="arrow-right" size={16} className="setup-aside__arrow" />
                </Link>
              </section>
            )}

            <footer className="setup-foot">
              <div className="setup-foot__text">
                {progress.step === 0
                  ? 'Not sure yet? You can skip this and come back from My settings.'
                  : progress.step === 1
                    ? 'You can finish setup later. Without an enabled personal or organization source, real aircraft tracking stays off. Practice scenarios still work. Pending organization requests need an admin’s approval.'
                    : 'That’s it — you’re ready for your first shoot.'}
              </div>
              <div className="setup-foot__actions">
                {progress.step < 2 ? (
                  <Button variant="ghost" disabled={busy} onClick={() => nav(next)}>
                    Finish later
                  </Button>
                ) : (
                  <Button variant="ghost" icon="arrow-left" disabled={busy} onClick={() => void save(progress.path, 1)}>
                    Back
                  </Button>
                )}
                {progress.step === 1 ? (
                  <Button variant="primary" iconRight="arrow-right" disabled={busy} onClick={() => void save(progress.path, 2)}>
                    Continue to first steps
                  </Button>
                ) : progress.step === 2 ? (
                  <Button variant="primary" icon="check" loading={busy} onClick={() => void save(progress.path, 2, true)}>
                    Finish setup
                  </Button>
                ) : null}
              </div>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}

/** One way of working, as a selectable card. Its accessible name is just the title; the description is its description. */
function PathCard({ icon, title, body, selected, disabled, onPick }: { icon: IconName; title: string; body: string; selected: boolean; disabled: boolean; onPick(): void }) {
  const id = useId();
  return (
    <button
      type="button"
      className={cx('path-card', selected && 'is-selected')}
      aria-labelledby={`${id}-t`}
      aria-describedby={`${id}-b`}
      disabled={disabled}
      onClick={onPick}
    >
      <span className="path-card__icon" aria-hidden>
        <Icon name={icon} size={22} />
      </span>
      <span className="path-card__text">
        <span className="path-card__title" id={`${id}-t`}>
          {title}
        </span>
        <span className="path-card__body" id={`${id}-b`}>
          {body}
        </span>
      </span>
      <span className="path-card__go" aria-hidden>
        {selected ? <span className="path-card__current">Current</span> : null}
        <Icon name="arrow-right" size={18} />
      </span>
    </button>
  );
}
